import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Fade from '@mui/material/Fade'
import TextField from '@mui/material/TextField'
import A from 'components/A'
import Avatar from 'components/Avatar'
import Paper from 'components/Paper'
import Icon from 'components/Icon'
import Button from 'components/Button'
import TextButtonMenu from 'components/TextButtonMenu'
import Type from 'components/Type'
import BouncingDots from 'components/CommonUI/BouncingDots'
import AddTripProtection from 'components/ModalViews/AddTripProtection'
import RequestWithdrawal from 'components/ModalViews/RequestWithdrawal'
import RequestReimbursement from 'components/ModalViews/RequestReimbursement'
import CreateVirtualCard from 'components/ModalViews/CreateVirtualCard'
import BoostAccount from 'components/ModalViews/BoostAccount'
import RequestCustomerService from 'components/ModalViews/RequestCustomerService'
import {
  IS_PRODUCTION,
} from 'utils/constants'
import { 
  GET_FAQ_CATEGORIES,
  GET_CHATBOT_RESPONSE,
} from 'api/constants'
import { useAction } from 'utils/actions'
import { useGTM, useTypeLoad } from 'utils/hooks'
import { isEmpty, map } from 'lodash'
import { useAffiliate } from 'utils/tripUtils'
import { addToChatHistory } from 'slices/customerSupportSlice'

const QUICK_LINKS = {
  admin_account_details: {
    group: 'Admin Tools',
    show: ({ user }) => user.is_admin,
    to: '/admin/account-details',
    name: 'Account Details',
  },
  admin_budgeting: {
    group: 'Admin Tools',
    show: ({ user }) => user.is_admin,
    to: '/admin/budgeting',
    name: 'Budgeting',
  },
  admin_stats: {
    group: 'Admin Tools',
    show: ({ user }) => user.is_admin,
    to: '/advanced-stats',
    name: 'Advanced Stats',
  },
  admin_user_management: {
    group: 'Admin Tools',
    show: ({ user }) => user.is_admin,
    to: '/admin/user-management',
    name: 'User Management',
  },
  admin_statements: {
    group: 'Admin Tools',
    show: ({ user }) => user.is_admin,
    to: '/admin/statements?view=account-activity',
    name: 'Corporate Statements',
  },
  connect_a_bank_account: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account,
    name: 'Funding Sources',
    to: '/wallet#linked-accounts',
  },
  create_a_wallet: {
    group: 'My Funds',
    show: ({ user }) => !user.has_savings_account,
    name: 'Create a Wallet',
    to: '/onboarding',
  },
  my_funds: {
    group: 'My Funds',
    show: () => true,
    to: '/wallet',
    name: 'My Wallet',
  },
  boost_account: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account,
    name: 'Boost My Account',
    modal: BoostAccount,
  },
  recurring_contribution: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account,
    to: '/wallet#fund-your-account',
    name: 'Fund Your Account',
  },
  match_history: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account && !user.is_donde_island && !user.is_club_donde,
    to: '/wallet#match-history',
    name: 'Match History',
  },
  virtual_card_management: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account,
    name: 'Virtual Card Management',
    to: '/wallet/virtual-card',
  },
  withdraw_money: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account,
    modal: RequestWithdrawal,
    name: 'Request to Withdraw Funds',
  },
  reimbursement: {
    group: 'My Funds',
    show: ({ user }) => user.has_savings_account,
    // modal: RequestWithdrawal,
    modal: RequestReimbursement,
    name: 'Request Reimbursement',
  },
  documents: {
    group: 'My Account',
    show: () => true,
    to: '/my-account#documents',
    name: 'View User Documents',
  },
  personal_account_information: {
    group: 'My Account',
    show: () => true,
    to: '/my-account',
    name: 'My Account',
  },
  my_trips: {
    group: 'My Trips',
    show: () => true,
    name: 'My Trips',
    to: '/trip-planning/my-trips',
  },
  manager_budgeting: {
    group: 'My Trips',
    show: ({ user }) => user.is_manager,
    to: '/budgets',
    name: 'My Budgets',
  },
  user_statements: {
    group: 'My Trips',
    show: ({ user }) => user.has_savings_account,
    to: '/statements/user',
    name: 'My Statements',
  },
  travel_insurance: {
    group: 'Travel',
    show: () => true,
    modal: AddTripProtection,
    name: 'Add Trip Protection',
  },
  book_cars: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/cars',
    name: 'Book Rental Cars',
  },
  book_cruise: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/cruises',
    name: 'Book Ocean Cruise',
  },
  book_experiences: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/things-to-do',
    name: 'Book Experiences',
  },
  book_flights: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/flights',
    name: 'Book Flights',
  },
  book_hotels: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/stays',
    name: 'Book Stays',
  },
  book_river_cruise: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/cruises?river=true',
    name: 'Book River Cruise',
  },
  book_disney: {
    group: 'Travel',
    show: () => true,
    name: 'Book Disney',
    affiliate: 'disney',
  },
  travel_planning: {
    group: 'Travel',
    show: () => true,
    to: '/?travel-questionnaire=true',
    name: 'Take the Travel Questionnaire',
  },
  book_outside_of_donde: {
    group: 'Travel',
    show: ({ user }) => user.has_savings_account,
    name: 'Create a Virtual Card',
    modal: CreateVirtualCard,
  },
  create_virtual_card: {
    group: 'Travel',
    show: ({ user }) => user.has_savings_account,
    hideInAll: true,
    name: 'Create a Virtual Card',
    modal: CreateVirtualCard,
  },
  donde_offers_affiliates_deals: {
    group: 'Travel',
    show: () => true,
    to: '/shop-travel/travel-partners',
    name: 'Offers and Affiliates',
  },
  report_a_bug: {
    group: 'Support',
    show: () => true,
    href: 'https://donde.typeform.com/to/cfkBfzUz',
    name: 'Report a Bug',
  },
  customer_support: {
    group: 'Support',
    show: () => true,
    modal: RequestCustomerService,
    name: 'Contact Customer Support',
  },
}

const agentQuickLink = {
  name: 'Contact Customer Support',
  modal: RequestCustomerService,
}

const AFFILIATES = {
  disney: {
    image: 'https://res.cloudinary.com/donde/image/upload/v1687209358/offers/offers_images/EBG_Disneyland_Resort_Passes_ifzxtv.jpg',
    title: 'Disneyland Resort Passes',
    cta: 'Explore offer',
    description: 'Save up to $35 on gate pass prices',
    url: 'https://memberdeals.com/godonde/pages.php?sub=disneyland&ebg_source=navigation&ebg_medium=topnavsub&ebg_campaign=disneyland',
    is_popular: false,
    provider: 'EBG',
    expiration: '',
    id: 271148,
    unique_id: '850b721b-ee69-4302-af6f-ae8359051d62',
    featured_sort_order: 0,
    favorited: false,
  },
}

const Message = ({
  src,
  flip,
  message,
  quickLinks,
  hideQuickLinks,
  hideSpiffy,
  allQuickLinks,
  onOpenModal,
  onAffiliateClick,
}) => {
  const messageRef = useRef()

  const copyMessage = () => {
    navigator.clipboard.writeText(messageRef.current.textContent)
  }

  const renderLinkHeader = () => 
    <Stack
      sx={theme => theme.internalMixins.columns({ gap: 1, align: 'flex-end' })}
    >
      <Type
        variant="data"
      >
        Helpful links
      </Type>
      <TextButtonMenu
        label="View all"
        options={allQuickLinks}
        renderInput={(item, value) => {
          const {
            to,
            modal,
            href,
          } = value?.key === 'agent' ? agentQuickLink : QUICK_LINKS[value?.key] || {}

          return (
            <A
              to={to}
              href={href}
              wrapComponent
              onClick={() => onOpenModal?.(modal)}
            >
              {item}
            </A>
          )
        }}
      />
    </Stack>

  return (
    <Stack
      gap={2}
    >
      <Stack
        sx={theme => ({
          ...(flip ? {
            ...theme.internalMixins.columns({ gap: 1, align: 'flex-end' }),
            '.avatar': {
              order: -1,
            },
          } : {
            ...theme.internalMixins.columns({ gap: 1, justify: 'flex-end', align: 'flex-end' }),
            textAlign: 'right',
          })
        })}
      >
        <Box
          onClick={copyMessage}
          sx={{
            py: 1,
            px: 1.5,
            borderRadius: '20px',
            ...(flip ? {
              bgcolor: 'rgba(98, 13, 62, .1)',
              color: 'rgba(98, 13, 62, 1)',
              borderBottomLeftRadius: 0,
            } : {
              bgcolor: 'rgba(98, 13, 62, 1)',
              color: 'text.inverse',
              borderBottomRightRadius: 0,
            })
          }}
        >
          <Type
            ref={messageRef}
            variant="data"
          >
            {typeof message === 'string' ? <span dangerouslySetInnerHTML={{ __html: message }} /> : message}
          </Type>
        </Box>
        {!!src?.length && (
          <>
            {hideSpiffy ? (
              <Box 
                className="avatar" 
                width="30px"
              />
            ) : (
              <Avatar
                src={src}
                // src={src}
                size="30px"
              />
            )}
          </>
        )}
      </Stack>
      {!hideQuickLinks && (
        <>
          {!isEmpty(quickLinks) && quickLinks.some(o => QUICK_LINKS.hasOwnProperty(o?.name || o)) ? (
            <Stack
              gap={1}
            >
              {renderLinkHeader()}
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={1}
              >
                {map(quickLinks, (o, key) => {
                  const {
                    to,
                    modal,
                    name,
                    href,
                    affiliate,
                  } = o === 'agent' ? agentQuickLink : QUICK_LINKS[o?.name || o] || {}

                  if (!name) return null

                  return (
                    <A
                      key={key + name}
                      to={to}
                      href={href}
                      wrapComponent
                      onClick={() => {
                        if (affiliate) onAffiliateClick(AFFILIATES[affiliate])
                        onOpenModal?.(modal)
                      }}
                    >
                      <Button
                        outlined
                        radius="full"
                        color="inverse"
                        size="shorter"
                        borderWidth="main"
                      >
                        {name}
                      </Button>
                    </A>
                  )  
                })}
              </Stack>
            </Stack>
          ) : (
            <Stack
              gap={1}
            >
              {renderLinkHeader()}
              <Button
                outlined
                radius="full"
                color="inverse"
                size="shorter"
                borderWidth="main"
                onClick={() => {
                  onOpenModal?.(QUICK_LINKS.customer_support.modal)
                }}
              >
                {QUICK_LINKS.customer_support.name}
              </Button>
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}

const SpiffyInput = ({
  onSendChat,
}) => {
  const [userInput, setUserInput] = useState('')

  const handleSubmit = data => {
    onSendChat(data)
    setUserInput('')
  }

  return (
    <TextField
      sx={{
        '.MuiInputBase-root': {
          borderRadius: '50px !important',
        }
      }}
      placeholder="Ask us anything…"
      InputProps={{
        endAdornment: (
          <Box 
            sx={{
              display: 'flex',
              pl: 1,
              py: 1,
              cursor: 'pointer',
            }}
            onClick={() => handleSubmit(userInput)}
          >
            <Icon
              icon="Reply"
              color="neutralCool_700"
              width="24"
              height="24"
            />
          </Box>
        ),
      }}
      value={userInput}
      onKeyDown={e => {
        if (e.key === 'Enter') handleSubmit(userInput)
      }}
      onChange={e => setUserInput(e.target.value)}
    />
  )
}

const TEST_QUESTIONS = [
//   {
//     question: `I can't find and book flights on Dónde`,
//     answer: `Over the last few years the airline industry has implemented frequent — and sometimes massive — changes in cancelation and modification policies. Relationships with third-party booking engines have worsened, making booking less user-friendly and frustrating for travelers like you.
// In light of these changes, and in pursuit of our mission to make travel easier, we are shifting our flight booking process to a virtual card payment system. That means you can book directly with airlines while still using your Dónde savings.`,
//   },
]

const Spiffy = ({
  hide,
}) => {
  const dispatch = useDispatch()
  const messagesEndRef = useRef()
  const location = useLocation()
  const {
    onAffiliateClick,
  } = useAffiliate()
  const { fireGTM } = useGTM({ event: 'Chat' })
  const { openModal, openChat, closeChat } = useAction()
  const user = useSelector(({ global }) => global.user)
  const company = useSelector(({ global }) => global.company)
  const spiffyOpen = useSelector(({ app }) => app.spiffyOpen)
  const faqCategories = useSelector(({ global }) => global.faqCategories)
  const chatHistory = useSelector(({ customerSupport }) => customerSupport.chatHistory)
  const [lastPageUsed, setLastPageUsed] = useState('')

  const { isLoading } = useTypeLoad(GET_CHATBOT_RESPONSE)

  const handleOnOpenModal = modal => {
    if (modal) {
      closeChat()
      const ActionModal = modal
      openModal(<ActionModal />)
    }
  }

  const setDataLayer = useCallback(() => {
    if (IS_PRODUCTION && lastPageUsed !== location.pathname) {
      fireGTM({ open: true, chatBot: true })
      setLastPageUsed(location.pathname)
    }
  }, [location.pathname])

  const filteredQuickLinks = useMemo(() => {
    return Object.keys(QUICK_LINKS)
      .filter(key => QUICK_LINKS[key].show({ user }))
      .reduce((res, key) => (res[key] = QUICK_LINKS[key], res), {})
  }, [user])

  const sendChat = userInput => {
    const newChat = { role: 'user', content: userInput }
    if (newChat.content?.length) {
      dispatch(addToChatHistory(newChat))
      dispatch({
        type: GET_CHATBOT_RESPONSE,
        data: {
          message: userInput,
          quick_links: Object.keys(filteredQuickLinks),
        },
      })
    }
  }

  useEffect(() => {
    if (spiffyOpen) {
      setDataLayer()
      if (isEmpty(faqCategories)) {
        dispatch({
          type: GET_FAQ_CATEGORIES,
        })
      }
    }
  }, [spiffyOpen])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [chatHistory])

  if (hide || location.pathname.includes('/trip-planning/my-trips/')) return null

  return (
    <Box
      sx={{
        position: 'fixed',
        width: 'calc(100vw - 20px)',
        height: 'calc(100vh - 20px)',
        bottom: 10,
        right: 10,
        zIndex: 'overlay',
        pointerEvents: 'none',
      }}
    >
      <Box
        role="button" 
        tabIndex="0"
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          display: {
            xx: 'none',
            md: 'inline-grid',
          },
          justifyItems: 'center',
          justifyContent: 'center',
          alignItems: 'center',
          alignContent: 'center',
          border: 'none',
          outline: 'none',
          cursor: 'pointer',
          borderRadius: '64px',
          width: '64px',
          height: '64px',
          boxShadow: '0 5px 11px 0 rgb(0 0 0 / 20%)',
          pointerEvents: 'all',
        }}
        onClick={() => openChat()}
      >
        <Icon
          icon="AIChatButton"
          width="64"
          height="64"
        />
      </Box>
      <Fade
        in={spiffyOpen}
      >
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            pointerEvents: 'all',
          }}
        >
          <Paper
            round="extra"
            depth="chat"
            sx={{
              width: '100%',
              maxWidth: 400,
            }}
          >
            <Box
              sx={theme => ({
                ...theme.internalMixins.columns({ justify: 'space-between', gap: 1 }),
                position: 'sticky',
                top: 0,
                px: .5,
                zIndex: 1,
              })}
            >
              <Stack
                sx={theme => theme.internalMixins.columns({ gap: 1 })}
                p={1}
              >
                <Avatar
                  src="/public/spiffy.svg"
                  size="35px"
                />
                <Stack
                  sx={theme => theme.internalMixins.columns({ gap: .5, align: 'flex-end' })}
                >
                  <Type
                    variant="bodyBold"
                  >
                    Spiffy
                  </Type>
                  <Type
                    variant="labelAtomicMedium"
                    color="text.neutral_500"
                  >
                    Powered by DóndeGPT
                  </Type>
                </Stack>
              </Stack>
              <Box
                role="button"
                tabIndex="0"
                sx={{
                  cursor: 'pointer',
                  p: 1,
                }}
                onClick={() => closeChat()}
              >
                <Icon 
                  icon="CloseLarge"
                  color="neutralCool_700"
                />
              </Box>
            </Box>
            <Stack
              display="grid"
              width="100%"
            >
              <Box
                display="grid"
                height={500}
                sx={{
                  overflow: 'auto',
                }}
              >
                <Stack
                  display="grid"
                  gridTemplateRows="1fr auto"
                  gap={2.5}
                  px={1.5}
                >
                  <Stack
                    display="grid"
                    gap={1}
                    justifyContent="center"
                    justifyItems="center"
                    textAlign="center"
                  >
                    <Avatar
                      src="/public/spiffy.svg"
                      size="80px"
                    />
                    <Stack
                      display="grid"
                      gap={.5}
                      justifyContent="center"
                      justifyItems="center"
                      textAlign="center"
                    >
                      <Type
                        variant="dataLargeBold"
                      >
                        Spiffy
                      </Type>
                      <Type
                        variant="data"
                        color="neutralCool_700"
                      >
                        I’m Dónde’s virtual assistant!
                      </Type>
                    </Stack>
                  </Stack>
                  <Stack
                    display="grid"
                    alignContent="flex-end"
                    gap={1}
                  >
                    <Message
                      src="/public/spiffy.svg"
                      flip
                      hideQuickLinks
                      hideSpiffy
                      message={`Hi, ${user.first_name}! 👋`}
                    />
                    <Message
                      src="/public/spiffy.svg"
                      flip
                      message={`How can I help you today?`}
                      quickLinks={['book_hotels', 'book_experiences', 'book_cars', 'book_cruise', 'book_flights', 'book_outside_of_donde', 'report_a_bug', 'travel_planning']}
                      allQuickLinks={filteredQuickLinks}
                      onOpenModal={handleOnOpenModal}
                    />
                    {chatHistory.map((o, idx) => (
                      <Message
                        key={`chatHistory_${idx}`}
                        src={o.role === 'assistant' ? '/public/spiffy.svg' : undefined}
                        // src={o.role === 'assistant' ? '/spiffy.svg' : user?.profile_image_url}
                        flip={o.role === 'assistant'}
                        hideQuickLinks={o.role === 'user'}
                        message={o.content}
                        quickLinks={o.quick_links}
                        allQuickLinks={filteredQuickLinks}
                        onOpenModal={handleOnOpenModal}
                        onAffiliateClick={data => {
                          onAffiliateClick(data)
                          closeChat()
                        }}
                      />
                    ))}
                    {isLoading && (
                      <Box
                        sx={theme => theme.internalMixins.columns({ gap: 1 })}
                      >
                        <Avatar
                          src="/public/spiffy.svg"
                          size="30px"
                        />
                        <BouncingDots />
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Stack>
                </Stack>
              </Box>
              <Box
                display="grid"
                px={1.5}
                pt={.5}
                pb={1.5}
              >
                <SpiffyInput
                  onSendChat={sendChat}
                />
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Fade>
    </Box>
  )
}

export default Spiffy